import { fabric } from 'fabric';
import { isUndefined } from 'lodash';
import Vec2 from 'vec2';

import {
  COORDS_EPSILON,
  DEFAULT_ZOOM,
  FREE_DRAWER_REAL_TIME_LENGTH_OFFSET,
  MINIMUM_GAP,
  MINIMUM_ZOOM,
  TEN_THOUSAND,
  ZOOM_MARGIN_ADJUSTMENT,
  ZOOM_PCT_CHANGE,
} from '~/core/constants';
import { LineDirection, isRadiusOut } from '~/core/enums';
import { DIRECTION } from '~/core/enums/drawing-types.enum';
import { roundToArrowLengthGranularity } from '~/core/helpers';
import {
  BoundingBox,
  createBoundingBox,
  getAbsoluteCenter,
  getHeight,
  getWidth,
} from '~/core/helpers/BoundingBox.utils';
import {
  addVectors,
  calculateRadiusFromArc,
  findBoundingBox,
  getDistanceBetween,
  getLinesFromPolygonPoints,
  getMidpoint,
  getVectorBetweenPoints,
  lineContainsPoint,
  linesIntersect,
  minDistanceToOtherPolys,
  multiplyVectorByScalar,
  pointsAreEqual,
  polygonCollisions,
} from '~/core/helpers/MathFunctions.utils';
import { SimpleLine } from '~/core/helpers/SimpleLine.utils';
import {
  ArrowInfo,
  CornerInfo,
  DrawingEdge,
  DrawingShape,
  LineSegment,
  Skeleton,
  SkeletonSeg,
} from '~/core/models';
import {
  getArrowShapeFromSegment,
  isCornerRadiusArc,
  isShapeGroup,
} from '~/core/utils';

/**
 * autoPlaceTranslation given a source polygon, find a translation vector that will place it in a non-colliding location
 * @param sourcePoly: an array of points defining the polygon to be translated
 * @param polyArray: an array of polygons (each an array of points) to test for collisions and attempt to avoid
 * @returns Vec2: a vector defining how much to translate the sourcePoly to avoid collisions
 */
export const autoPlaceTranslation = (
  sourcePoly: Vec2[],
  polyArray: Vec2[][],
  ignoreIndex?: number
): Vec2 => {
  // copy the original polygon so we can translate it if necessary
  let testPoly = sourcePoly.map((value) => value);
  let result = new Vec2(0, 0);
  let collisions = polygonCollisions(testPoly, polyArray);

  if (
    !collisions.some((x) => x) &&
    minDistanceToOtherPolys(testPoly, polyArray) > MINIMUM_GAP
  ) {
    return result;
  }

  // bounding boxes for all polygons in the array
  const polyBounds = polyArray.map((value) => findBoundingBox(value));
  // and their center points
  const polyCenters = polyBounds.map(
    (value) => getAbsoluteCenter(value) || new Vec2(0, 0)
  );

  // find the original dirVector which is the sum of the vectors between overlapping polygon centers
  // and the source center
  const sourceBounds = findBoundingBox(sourcePoly) || createBoundingBox();
  const sourceCenter = getAbsoluteCenter(sourceBounds);
  let dirVector = new Vec2(0, 0);

  polyCenters.forEach((bCenter, index) => {
    if (collisions[index]) {
      const aVec = getVectorBetweenPoints(sourceCenter, bCenter);

      dirVector.add(aVec.normalize());
    }
  });

  // if they're directly on top of each other, shift (right, down)
  if (dirVector.length() === 0) {
    dirVector = new Vec2(1, 1);
  }

  const shiftDistance = 10; // should be calculated to minimize attempts
  const scaledDirVector = dirVector.normalize(true).multiply(shiftDistance);

  // console.debug('initial collisions', collisions);
  let attempts = 50;

  while (
    (attempts-- && collisions.some((x) => x)) ||
    minDistanceToOtherPolys(testPoly, polyArray) < MINIMUM_GAP
  ) {
    testPoly = testPoly.map((point) => point.subtract(scaledDirVector, true));
    collisions = polygonCollisions(testPoly, polyArray, ignoreIndex);
    // console.debug('loop', attempts, testPoly);
    // console.debug('loop collisions', collisions);
  }

  // final result is the delta from any two common points of the source polygon and our translated test
  result = getVectorBetweenPoints(testPoly[0], sourcePoly[0]);

  return result;
};

const getUpperLeftLowerRightPoints = (
  o: fabric.Object
): [{ x: number; y: number }, { x: number; y: number }] => {
  const b = o.getBoundingRect(true, true);

  return [
    { x: b.left, y: b.top },
    { x: b.left + b.width, y: b.top + b.height },
  ];
};

type CanvasPoint = { x: number; y: number };
type BoundingBoxPoints = [CanvasPoint, CanvasPoint];

/**
 * Adjust bounding box points for a group based on its center point
 * @param child the child object relative to the group
 * @param groupCenterVec2 the center of a group of fabric objects
 * @returns the bounding box coordinates
 */
const adjustPointsForGroup =
  (groupCenterVec2: Vec2) =>
  (child: fabric.Object): BoundingBoxPoints => {
    const [upperLeft, lowerRight] = getUpperLeftLowerRightPoints(child).map(
      (point) => new Vec2(point.x, point.y).add(groupCenterVec2, true)
    );

    return [
      { x: upperLeft.x, y: upperLeft.y },
      { x: lowerRight.x, y: lowerRight.y },
    ];
  };

/**
 * Process a fabric group object and return bounding box points for its children
 * @param group the group of fabric objects
 * @returns the bounding box points for the fabric group object children without the corner radius arcs
 */
const adjustPointsForNonRadiusArcObjects = (
  group: fabric.Group
): CanvasPoint[] => {
  const groupCenterPoint = group.getCenterPoint();
  const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);

  return group
    .getObjects()
    .filter((child: fabric.Object) => !isCornerRadiusArc(child))
    .flatMap(adjustPointsForGroup(groupCenterVec2));
};

/**
 * Calculates the canvas bounding box coordinates
 * @param canvas the fabric canvas object
 * @returns the canvas bounding box coordinates
 */
export const getCanvasBoundingBox = (canvas?: fabric.Canvas): BoundingBox => {
  const canvasObjects = canvas?.getObjects() ?? [];
  const canvasPoints = canvasObjects
    .flatMap((obj) =>
      // find bounding box points for the given object (or group)
      isShapeGroup(obj)
        ? adjustPointsForNonRadiusArcObjects(obj as fabric.Group)
        : getUpperLeftLowerRightPoints(obj)
    )
    .flat();

  return findBoundingBox(canvasPoints as Vec2[]);
};

/**
 * Zooms and/or pans the canvas to fit all objects.
 * Zooms to the scale to fit the image in the smallest dimension.  Pans to the center of all objects on the canvas
 * @param canvas - the canvas to auto zoom or pan
 * @param zoom - whether to zoom to fit all the elements
 * @param center - whether to center the canvas on the center of all the elements in the canvas
 */
export const autoZoomPanToFit = (
  canvas: fabric.Canvas,
  zoom: boolean,
  center: boolean
) => {
  if (
    isUndefined(canvas) ||
    !canvas ||
    !canvas.getObjects() ||
    (!zoom && !center)
  ) {
    return;
  }
  const boundingBox = getCanvasBoundingBox(canvas);

  if (!boundingBox) {
    return;
  }
  const bWidth = getWidth(boundingBox);
  const bHeight = getHeight(boundingBox);

  if (zoom && bWidth && bHeight) {
    const xRatio = bWidth / canvas.getWidth() / ZOOM_MARGIN_ADJUSTMENT;
    const yRatio = bHeight / canvas.getHeight() / ZOOM_MARGIN_ADJUSTMENT;
    const zoomRatio = Math.max(xRatio, yRatio);
    const zoomLevel = Math.max(MINIMUM_ZOOM, 1 / zoomRatio);

    canvas.setZoom(zoomLevel);
  }
  if (center) {
    const currentZoom = canvas.getZoom();

    canvas.absolutePan({
      x:
        boundingBox.left * currentZoom -
        (canvas.getWidth() - bWidth * currentZoom) / 2,
      y:
        boundingBox.top * currentZoom -
        (canvas.getHeight() - bHeight * currentZoom) / 2,
    });
  }
};

/**
 * Zoom the canvas IN based on the zoomByStep function
 * TODO does not center
 * @param canvas the canvas to zoom
 * @param center recenter the canvas to this point
 */
export const zoomIn = (canvas: fabric.Canvas, center?: Vec2) => {
  if (!canvas) {
    return;
  }
  if (center) {
    canvas.absolutePan(center);
  }
  canvas.setZoom(zoomByStep(canvas, 1));
  autoZoomPanToFit(canvas, false, true);
};

/**
 * Zoom the canvas OUT based on the zoomByStep function
 * TODO these should probably include a Point parameter too that centers the zoom on that point with absolutePan
 * @param canvas the canvas to zoom
 * @param center recenter the canvas to this point
 */
export const zoomOut = (canvas: fabric.Canvas, center?: Vec2) => {
  if (!canvas) {
    return;
  }
  if (center) {
    canvas.absolutePan(center);
  }
  canvas.setZoom(zoomByStep(canvas, -1));
  autoZoomPanToFit(canvas, false, true);
};

/**
 * creates a defined array of steps for zooming based around a mid-point value and stepping up/down by a defined %
 * the array will extend the same number of steps on either side of the middle until the min value is reached
 * Invalid values for min, max, pctChange are sanitized to something mathematically useful
 * NOTE: this used to make the zoom levels deterministic
 * @param min - minimum zoom value (0 < min < middle)
 * @param middle - the midpoint zoom value for the array (0 < min < middle)
 * @param pctChange - how much each step should change (0.1=10%) value (0 < pctChange < 1)
 */
const makeZoomStepArray = (
  min: number,
  middle: number,
  pctChange: number
): Array<number> => {
  const adjMiddle = Math.abs(middle) > 0 ? Math.abs(middle) : 1; // fallback to 1 because, whatever - this should probably error
  const adjPct = pctChange > 0 && pctChange < 1 ? pctChange : 0.2; // default 20%
  const adjMin = min > 0 && min < adjMiddle ? min : adjPct / 2; // fallback is half the % change

  const steps = new Array(1).fill(adjMiddle);

  for (let i = 0; steps[0] > adjMin; i++) {
    steps.splice(0, 0, steps[0] * (1 - adjPct));
    steps.push(steps[steps.length - 1] * (1 + adjPct));
  }

  return steps;
};

/**
 * returns the value to set the zoom (it does not actually zoom the canvas!!!!) to zoom in or out from the current level
 * with "zoom to fit" as an option, the current zoom level may not match one of our defined steps, but this will step to the
 * next closest value
 * NOTE: This relies on a ZOOM_STEPS array (which gets generated every time right now) that includes all the valid steps
 * NOTE: there's a "straight math" version of this commented out that works fine, except it drifts from any defined value
 * @param canvas the canvas to zoom
 * @param direction direction of the zoom, -1 to zoom out, +1 to zoom in
 */
const zoomByStep = (canvas: fabric.Canvas, direction: number): number => {
  if (!canvas) {
    return DEFAULT_ZOOM;
  } // no canvas yet, you shouldn't be calling this yet, but here's a value

  // straight math version, drifts from fixed values (never returns to DEFAULT_ZOOM)
  // return canvas.getZoom() * (1 + Math.sign(direction) * 0.05);

  // TODO: make ZOOM_STEPS global instead of rebuilding each time if we're going to use defined steps
  // make the array of zoom values, starting from dEFAULT_ZOOM and working down to MINIMUM_ZOOM
  // this shouldn't be calculated each time in the real app
  const ZOOM_STEPS = makeZoomStepArray(
    MINIMUM_ZOOM,
    DEFAULT_ZOOM,
    ZOOM_PCT_CHANGE
  );

  if (direction === 0) {
    return canvas.getZoom();
  }

  const currentZoom = canvas.getZoom();
  const currentIndex = ZOOM_STEPS.findIndex((value) => value >= currentZoom);

  // finds the next value from where you're currently zoomed
  // allows auto-zoom to set an arbitrary value, but then get back to the fixed steps
  const nextZoomLevel = () => {
    if (direction < 0) {
      if (currentIndex === 0 || currentZoom < ZOOM_STEPS[0]) {
        return currentZoom;
      } // we're at or below the minimum, so stay there
      if (currentIndex === -1) {
        return ZOOM_STEPS[ZOOM_STEPS.length - 1];
      } // we're above the max, so go to the max

      return ZOOM_STEPS[currentIndex - 1]; // we're in the array, so step down one
    } else if (direction > 0) {
      if (currentZoom < ZOOM_STEPS[0]) {
        return ZOOM_STEPS[0];
      } // we're below the minimum, so go to the minimum
      if (currentIndex === -1) {
        return currentZoom;
      } // we're at or above the max, so stay there
      if (
        currentIndex === ZOOM_STEPS.length - 1 &&
        currentZoom <= ZOOM_STEPS[ZOOM_STEPS.length - 1]
      ) {
        return ZOOM_STEPS[ZOOM_STEPS.length - 1];
      } // we're at or just below the max, so go to max
      if (currentZoom < ZOOM_STEPS[currentIndex]) {
        return ZOOM_STEPS[currentIndex];
      } // we're between steps, so bump to the next

      return ZOOM_STEPS[currentIndex + 1]; // we're in the array, so step up one
    }

    return currentZoom;
  };

  return nextZoomLevel();
};

export const cleanSkeletonPoints = (sk: Skeleton): Skeleton | undefined => {
  let deDupe = sk
    .filter((s, i, a) => {
      if (i === 0) {
        return true;
      }
      if (i === a.length - 1 && pointsAreEqual(a[0].pos, s.pos)) {
        return false;
      }

      return !(i > 0 && pointsAreEqual(a[i - 1].pos, s.pos));
    })
    .map((bone) => ({ ...bone, pos: new Vec2(bone.pos.x, bone.pos.y) }));

  while (deDupe) {
    // started empty or reduced to a single point, it's an invalid skeleton
    if (!deDupe || deDupe.length === 1) {
      return undefined;
    }
    // if < 3 points, no further optimization can be done
    if (deDupe.length < 3) {
      return deDupe;
    }

    const removals: number[] = [];

    for (let i = 0; i < deDupe.length - 2; i++) {
      const [a, b, c] = [deDupe[i].pos, deDupe[i + 1].pos, deDupe[i + 2].pos];

      // if next three points are all horizontal or vertical and in the same line, remove the middle one
      if (
        ((a.x === b.x && b.x === c.x) || (a.y === b.y && b.y === c.y)) &&
        lineContainsPoint(new SimpleLine(a, c), b, true, true) &&
        !removals.includes(i + 1)
      ) {
        removals.push(i + 1);
      }
    }

    if (removals.length === 0) {
      return deDupe;
    }

    deDupe = deDupe.filter((_value, index) => !removals.includes(index));
  }

  return undefined;
};

export const getOutlinePts = (
  sk: Skeleton
): Array<Partial<CornerInfo>> | undefined => {
  if (!sk || sk.length < 2) {
    return undefined;
  }

  const points = [];

  for (let i = 1; i < sk.length; i++) {
    const startPt = new Vec2(sk[i - 1].pos.x, sk[i - 1].pos.y);
    const endPt = new Vec2(sk[i].pos.x, sk[i].pos.y);

    let U = new Vec2(0, 0);
    let V = new Vec2(0, 0);

    const diffVec = new Vec2(endPt.x - startPt.x, endPt.y - startPt.y);
    const normalVec = diffVec
      .normalize(true)
      .rotate(Math.PI / 2, undefined, true);

    const dPrev = sk[i - 1].depth;

    let angle;

    if (i === 1) {
      U = startPt.add(normalVec.multiply(dPrev, true), true);
      V = startPt.subtract(normalVec.multiply(dPrev, true), true);

      points.push(
        {
          coords: U,
          skIndex: i - 1,
          isUpper: true,
          angle,
        },
        {
          coords: V,
          skIndex: i - 1,
          isUpper: false,
          angle,
        }
      );
    }

    if (i === sk.length - 1) {
      U = endPt.add(normalVec.multiply(dPrev, true), true);
      V = endPt.subtract(normalVec.multiply(dPrev, true), true);
    } else {
      const nextPt = new Vec2(sk[i + 1].pos.x, sk[i + 1].pos.y);
      const nextDiffVec = new Vec2(nextPt.x - endPt.x, nextPt.y - endPt.y);

      const dNext = sk[i].depth;

      angle = diffVec.multiply(-1, true).angleTo(nextDiffVec);
      if (angle === undefined || !angle) {
        console.warn(
          'Undefined angle:',
          sk[i],
          sk[i + 1],
          diffVec,
          nextDiffVec
        );
      }
      const alpha = Math.atan(
        (dPrev * Math.sin(angle)) / (dNext + dPrev * Math.cos(angle))
      );

      const diffVecLength = Math.sqrt(
        diffVec.x * diffVec.x + diffVec.y * diffVec.y
      );
      const normalizedDiffVec = new Vec2(
        diffVecLength > 0 ? diffVec.x / diffVecLength : 0,
        diffVecLength > 0 ? diffVec.y / diffVecLength : 0
      );

      const cosAlpha = Math.cos(alpha);
      const sinAlpha = Math.sin(alpha);
      const dirVec = new Vec2(
        normalizedDiffVec.x * cosAlpha - normalizedDiffVec.y * sinAlpha,
        normalizedDiffVec.x * sinAlpha + normalizedDiffVec.y * cosAlpha
      );
      const length = alpha === 0 ? dPrev : dPrev / Math.sin(alpha);

      U = endPt.add(dirVec.multiply(length, true), true);
      V = endPt.subtract(dirVec.multiply(length, true), true);
    }

    points.splice(
      points.length / 2,
      0,
      {
        coords: U,
        skIndex: i,
        isUpper: true,
        angle,
      },
      {
        coords: V,
        skIndex: i,
        isUpper: false,
        angle,
      }
    );
  }

  return points;
};

export const getRectangleSideItemArrowsFromOutlines = (
  points: Vec2[],
  label?: string
): ArrowInfo[] => {
  const arrows = [];

  const newPoints = points.slice(0);

  newPoints.push(points[0]);

  const ltPoint = 0;
  const rtPoint = 1;
  const rbPoint = 2;
  const lbPoint = 3;

  for (let i = 1; i < newPoints.length; i++) {
    const startPt = newPoints[i - 1];
    const endPt = newPoints[i];

    if (startPt !== newPoints[rbPoint] && endPt !== newPoints[lbPoint]) {
      let backsplashLetter = '';
      const arrowShapePoints = getArrowShapeFromSegment(startPt, endPt);

      if (startPt === newPoints[ltPoint] && endPt === newPoints[rtPoint]) {
        backsplashLetter = label ?? '';
      }
      arrows.push({ path: arrowShapePoints, label: backsplashLetter });
    }
  }

  return arrows;
};

export const getBumpOutArrowsFromOutlines = (points: Vec2[]): ArrowInfo[] => {
  const arrows = [];

  const newPoints = points.slice(0);

  newPoints.push(points[0]);

  for (let i = 1; i < newPoints.length; i += 2) {
    const arrowShapePoints = getArrowShapeFromSegment(
      newPoints[i - 1],
      newPoints[i]
    );

    arrows.push({ path: arrowShapePoints, label: '' });
  }

  return arrows;
};

export const getDirection = (line: LineSegment): string => {
  const diffVec = new Vec2(
    line.endPoint.x - line.startPoint.x,
    line.endPoint.y - line.startPoint.y
  );

  let direction = LineDirection.TOP;

  if (Math.abs(diffVec.y) < Math.abs(diffVec.x) * 0.0875) {
    //Above condition is for checking which direction current edge is laid towards, more likely towards the xAxis or yAxis
    //With this information, we will put measurement text horizontally or vertically
    // In other words, the case for angle between the diff vector and x axis is less than 5'
    if (diffVec.x > 0) {
      direction = LineDirection.BOTTOM;
    } else {
      direction = LineDirection.TOP;
    }
  } else if (diffVec.y < 0) {
    if (Math.abs(diffVec.x) < COORDS_EPSILON) {
      direction = LineDirection.VERTICAL_RIGHT;
    } else {
      direction = LineDirection.RIGHT;
    }
  } else if (Math.abs(diffVec.x) < COORDS_EPSILON) {
    direction = LineDirection.VERTICAL_LEFT;
  } else {
    direction = LineDirection.LEFT;
  }

  return direction;
};

export const getOutlinePointsFromShape = (
  dShape: DrawingShape
): Array<Vec2> => {
  if (!dShape?.cornerMap) {
    return [];
  }

  return Object.values(dShape.cornerMap).map((corner) => corner.coords);
};

const addCustomEdgeVirtualCorner = (
  cornerIn: Vec2,
  edge: DrawingEdge
): Vec2 | undefined => {
  const addDepth = isRadiusOut(edge) ? edge.radiusSagitta : edge.bumpOut?.depth;

  return addDepth
    ? cornerIn.add(edge.normalVec.multiply(addDepth, true), true)
    : undefined;
};

const getBigArcPoints = (
  edge: DrawingEdge,
  usePrevEdge: boolean
): [Vec2, Vec2] | undefined => {
  if (!isRadiusOut(edge)) {
    return undefined;
  }

  const chordLength = getDistanceBetween(
    edge.startDisplayVec,
    edge.endDisplayVec
  );
  const radiusSagitta = edge.radiusSagitta as number;
  const bigArc = radiusSagitta > chordLength / 2;

  if (!bigArc) {
    return undefined;
  }

  const changeSign = usePrevEdge ? 1 : -1;
  const radius = calculateRadiusFromArc(chordLength, radiusSagitta);
  const chordMidPoint = getMidpoint(edge.startDisplayVec, edge.endDisplayVec);
  const bigArcPoint0 = chordMidPoint.add(
    edge.dirVec.normalize(true).multiply(radius * changeSign, true),
    true
  );
  const bigArcPoint1 = bigArcPoint0.add(
    edge.normalVec.multiply(radiusSagitta, true),
    true
  );

  return [bigArcPoint0, bigArcPoint1];
};

const getEdgePts = (dir: 'prev' | 'next', vec: Vec2, edge?: DrawingEdge) => {
  if (edge?.customSideType === undefined) {
    return [];
  }

  const bigArcPoints = getBigArcPoints(edge, dir === 'prev');
  const handle = {
    prev: () => [
      addCustomEdgeVirtualCorner(vec, edge),
      ...(bigArcPoints ? [bigArcPoints[1], bigArcPoints[0]] : []),
    ],
    next: () => [
      ...(bigArcPoints ? [bigArcPoints[0], bigArcPoints[1]] : []),
      addCustomEdgeVirtualCorner(vec, edge),
    ],
  };

  return handle[dir]?.() ?? [];
};

/**
 * Conditionals for arrows
 * * If edge before this point is a radius out, then
 *   * add a point using normVec on that edge
 *     * unless this is an end replacement point, then don't
 * * If edge after this point is a radius out, then
 *   * add a point using normVec on that edge
 *     * unless this is a start replacement point, then don't
 * * If neither of the above is true, then
 *   * just add the regular point, or replacement points as
 */
export const getOutlinePointsFromModifiedShape = (
  drawingShape: DrawingShape,
  forArrows = false
): Vec2[] => {
  if (forArrows) {
    return Object.values(drawingShape.cornerMap).flatMap((corner) =>
      corner.diagonalLength &&
      corner.isVisibleInCanvas &&
      corner.replacementStartCoords &&
      corner.replacementEndCoords
        ? [corner.replacementStartCoords, corner.replacementEndCoords]
        : [corner.coords]
    );
  }

  type OptionalVec = Vec2 | undefined;
  type Pair = [OptionalVec[], OptionalVec[]];

  return Object.values(drawingShape.cornerMap)
    .flatMap((corner) => {
      const hasValidLength =
        (corner.diagonalLength || (corner.size ?? 0) > 0.75) &&
        corner.isVisibleInCanvas;
      const [prevCoords, nextCoords] = ['prev', 'next'].map((dir) =>
        hasValidLength
          ? ((dir === 'prev'
              ? corner.replacementStartCoords
              : corner.replacementEndCoords) as Vec2)
          : corner.coords
      );
      const [prevPts, nextPts] = drawingShape.drawingEdges.reduce<Pair>(
        ([prev, next], edge) => {
          const handle: Record<string, Pair> = {
            [edge.endCornerId]: [getEdgePts('prev', prevCoords, edge), next],
            [edge.startCornerId]: [prev, getEdgePts('next', nextCoords, edge)],
          };

          return handle[corner.id] ?? [prev, next];
        },
        [[], []]
      );
      const regularPts =
        hasValidLength && (corner.angle ?? 0) < 0
          ? [corner.replacementStartCoords, corner.replacementEndCoords]
          : [corner.coords];

      return [...prevPts, ...regularPts, ...nextPts];
    })
    .filter(Boolean) as Vec2[];
};

/**
 * Returns a list with the id's of the corners that have a diagonal applied
 * @param drawingShape
 * @returns the list of corners that have a diagonal applied
 */
export const getOutlineDiagonalFlagsFromModifiedShape = (
  dShape: DrawingShape
) =>
  Object.values(dShape.cornerMap).reduce<{
    diagonals: boolean[];
    cornerIds: (CornerInfo['id'] | undefined)[];
  }>(
    (acc, corner) =>
      corner.diagonalLength && corner.isVisibleInCanvas
        ? {
            diagonals: [...acc.diagonals, false, true],
            cornerIds: [...acc.cornerIds, corner.id, corner.id],
          }
        : {
            diagonals: [...acc.diagonals, false],
            cornerIds: [...acc.cornerIds, undefined],
          },
    { diagonals: [], cornerIds: [] }
  );

/**
 * @param drawingShape
 * @returns the list of outline edges where a custom side was applied (bump out or radius out)
 */
export const getOutlineCustomSideEdges = (
  drawingShape: DrawingShape
): (DrawingEdge | undefined)[] =>
  Object.values(drawingShape.cornerMap).flatMap((corner) => {
    const prevEdge = drawingShape.drawingEdges.find(
      (edge) => edge.startCornerId === corner.id
    );
    const customSide =
      prevEdge?.customSideType !== undefined ? prevEdge : undefined;

    return corner.diagonalLength && corner.isVisibleInCanvas
      ? [undefined, customSide]
      : [customSide];
  });

export const getRefinedEdgePoints = (
  [startX, startY, endX, endY]: Array<number>,
  stroke?: number
) => {
  const offset = stroke ? stroke * 0.5 : 0;

  return [startX - offset, startY - offset, endX - offset, endY - offset];
};

export const freeDrawerRealTimeLength = (
  points: Array<Vec2>
): { length: number; pos: Vec2 } => {
  let length = 0.0;
  let dirVec = new Vec2(0, 0);

  for (let i = 1; i < points.length; i++) {
    const diff = new Vec2(
      points[i].x - points[i - 1].x,
      points[i].y - points[i - 1].y
    );
    const d = diff.length();

    length += d;
    if (i === points.length - 1) {
      const diffLength = diff.length();

      dirVec = new Vec2(
        diffLength > 0 ? diff.x / diffLength : 0,
        diffLength > 0 ? diff.y / diffLength : 0
      );
    }
  }

  const roundedValue = roundToArrowLengthGranularity(length);
  const newPos = addVectors(
    points[points.length - 1],
    multiplyVectorByScalar(dirVec, FREE_DRAWER_REAL_TIME_LENGTH_OFFSET)
  );

  return { length: roundedValue, pos: newPos };
};

export const generateSkeletonData = (
  points: Array<Vec2>,
  depth: number
): Skeleton =>
  points.map(
    (pt: Vec2) =>
      ({
        pos: new Vec2(pt.x, pt.y),
        depth,
      }) as SkeletonSeg
  );

// Get upper, bottom, left, right direction vector for given vector
export const isCorrectDirection = (vec: Vec2, direction: string): boolean => {
  let result = true;
  let dirVec = new Vec2(1, 0);

  switch (direction) {
    case DIRECTION.UP:
      dirVec = new Vec2(0, -1);
      break;
    case DIRECTION.DOWN:
      dirVec = new Vec2(0, 1);
      break;
    case DIRECTION.LEFT:
      dirVec = new Vec2(-1, 0);
      break;
    case DIRECTION.RIGHT:
      dirVec = new Vec2(1, 0);
      break;
  }

  if (vec.x * dirVec.x + vec.y * dirVec.y <= 0) {
    result = false;
  }

  return result;
};

/**
 * Checks whether @param testPoint is inside @param polygon
 * or if it's part of its edges
 */
export const isPointInsidePolygon = (
  polygon: Vec2[],
  testPoint: Vec2
): boolean => {
  const targetLines = getLinesFromPolygonPoints(polygon);
  const sourceLine = new SimpleLine(
    testPoint,
    new Vec2(testPoint.x, TEN_THOUSAND)
  );

  const isPointPartOfThePolygon = targetLines?.some((targetLine) =>
    new Vec2(testPoint)?.isPointOnLine(targetLine.point1, targetLine.point2)
  );

  const intersectionCount = targetLines?.reduce(
    (numberOfIntersections, targetLine) =>
      numberOfIntersections + (linesIntersect(sourceLine, targetLine) ? 1 : 0),
    0
  );

  // if an even number of intersections are found, the point is not inside the polygon
  return !(intersectionCount % 2 === 0) || isPointPartOfThePolygon;
};
